import React, { useState, ReactNode } from "react";
import { handleClickSignIn, handleClickConfirmSignIn } from "../lib/auth";

// jotai
import { useSetAtom } from "jotai";
import { AppAtoms } from "../lib/store";

// ui
import {
  Box,
  InputAdornment,
  IconButton,
  CircularProgress,
} from "@mui/material";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import Visibility from "@mui/icons-material/Visibility";
// ★cmd-po_1208: 意匠 Po*/catalog 化（PoField/PoLabel/PoTextField/PoButton・認証ロジック不触）
import { PoButton } from "../component/common/PoButton";
import { PoField, PoLabel, PoTextField } from "../component/common/PoForm";

// api
import axios from "axios";
import { getProfile } from "../api/profile";
import { getScreenColumns } from "../api/screenColumn";
import { CONST } from "../config/const";

// 認証確認
import { fetchAuth } from "../lib/auth";

function Login() {
  const setIsLogin = useSetAtom(AppAtoms.isLogin);
  const setLoginUser = useSetAtom(AppAtoms.loginUser);
  const setContractScreenColumn = useSetAtom(AppAtoms.contractScreenColumn);
  const setTossUpScreenColumn = useSetAtom(AppAtoms.tossUpScreenColumn);
  const setProfileLoaded = useSetAtom(AppAtoms.profileLoaded);
  const setTenantPlan = useSetAtom(AppAtoms.tenantPlanAtom);
  const setAccountRunkingScreenColumn = useSetAtom(AppAtoms.accountRunkingScreenColumn);
  const setSampleQuestions = useSetAtom(AppAtoms.sampleQuestionsAtom);

  const [onLogin, setOnLogin] = useState<boolean>(true);

  // ログイン画面
  const [Id, setId] = useState<string>("");
  const [pass, setPass] = useState<string>("");
  const [showPassword, setShowPassword] = useState<boolean>(false);

  // パスワード変更画面
  const [newpass1, setNewpass1] = useState<string>("");
  const [showPassword1, setShowPassword1] = useState<boolean>(false);
  const [newpass2, setNewpass2] = useState<string>("");
  const [showPassword2, setShowPassword2] = useState<boolean>(false);

  const [message, setMessage] = useState<ReactNode>("");
  const [loading, setLoading] = useState<boolean>(false);

  async function getScreenColumn() {
    const {
      contractScreenColumn,
      tossUpScreenColumn,
      accountRunkingScreenColumn,
    } = await getScreenColumns();
    setContractScreenColumn(contractScreenColumn);
    setTossUpScreenColumn(tossUpScreenColumn);
    setAccountRunkingScreenColumn(accountRunkingScreenColumn);
  }

  async function login() {
    setLoading(true);
    const signInRes: string = await handleClickSignIn(Id, pass);
    if (signInRes === "Login") {
      const { login, loginUser } = await fetchAuth();
      setLoginUser(loginUser);
      setIsLogin(login);
      if (loginUser) {
        getScreenColumn();
        axios.get(CONST.apiUrl2 + "sampleQuestions")
          .then((res) => {
            if (res.data.questions_by_page) {
              setSampleQuestions(res.data.questions_by_page);
            }
          })
          .catch(() => {});
        try {
          const profile = await getProfile();
          setLoginUser({
            ...loginUser,
            db_authority: profile.authority,
            username: profile.account_name,
          });
          setTenantPlan(
            profile.plan_id != null
              ? {
                  plan_id: profile.plan_id,
                  plan_name: profile.plan_name ?? "",
                  max_partners: profile.max_partners ?? 0,
                  max_et_sites: profile.max_et_sites ?? 0,
                  has_payment: profile.has_payment ?? false,
                  has_ai_analysis: profile.has_ai_analysis ?? false,
                  current_partners: profile.current_partners ?? 0,
                  current_et_sites: profile.current_et_sites ?? 0,
                  monthly_price: profile.monthly_price ?? 0,
                }
              : null,
          );
        } finally {
          setProfileLoaded(true);
        }
      }
    } else if (signInRes === "NextStep") {
      setIsLogin(false);
      setOnLogin(false);
      setMessage("初期パスワード変更してください。");
    } else if (signInRes === "Error") {
      setIsLogin(false);
      setMessage("IDまたはパスワードが違います。");
    }
    setLoading(false);
  }

  async function changePassword() {
    if (!newpass1 || !newpass2) {
      setMessage(
        <div>
          <div>新規パスワードを入力してください。</div>
        </div>
      );
      return;
    } else if (newpass1 !== newpass2) {
      setMessage(
        <>
          新規パスワードが確認用パスワードと
          <br />
          一致していません。
        </>
      );
      return;
    } else if (newpass1 === pass) {
      setMessage(
        <>
          現在使用しているパスワードは
          <br />
          新規パスワードに指定できません。
        </>
      );
      return;
    }
    setLoading(true);
    const signInRes = await handleClickConfirmSignIn(newpass1);
    if (signInRes === "Login") {
      const { login, loginUser } = await fetchAuth();
      setLoginUser(loginUser);
      setIsLogin(login);
      if (loginUser) {
        getScreenColumn();
        axios.get(CONST.apiUrl2 + "sampleQuestions")
          .then((res) => {
            if (res.data.questions_by_page) {
              setSampleQuestions(res.data.questions_by_page);
            }
          })
          .catch(() => {});
        try {
          const profile = await getProfile();
          setLoginUser({
            ...loginUser,
            db_authority: profile.authority,
            username: profile.account_name,
          });
          setTenantPlan(
            profile.plan_id != null
              ? {
                  plan_id: profile.plan_id,
                  plan_name: profile.plan_name ?? "",
                  max_partners: profile.max_partners ?? 0,
                  max_et_sites: profile.max_et_sites ?? 0,
                  has_payment: profile.has_payment ?? false,
                  has_ai_analysis: profile.has_ai_analysis ?? false,
                  current_partners: profile.current_partners ?? 0,
                  current_et_sites: profile.current_et_sites ?? 0,
                  monthly_price: profile.monthly_price ?? 0,
                }
              : null,
          );
        } finally {
          setProfileLoaded(true);
        }
      }
    } else if (signInRes === "Error") {
      setIsLogin(false);
      setMessage(
        <>
          パスワードの要件を満たしていません。
          <br />
          大文字・小文字・数字・記号を含む
          <br />
          8文字以上の文字列をご入力ください。
        </>
      );
    }
    setLoading(false);
  }

  return (
    <Box
      className="login-wrap"
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--grad-hero)',
        p: 'var(--sp-6) var(--sp-4)',
      }}
    >
      <Box
        component="form"
        className="login-card"
        onSubmit={(e: React.FormEvent) => {
          e.preventDefault();
          if (onLogin) {
            login();
          } else {
            changePassword();
          }
        }}
        sx={{
          width: '100%',
          maxWidth: 400,
          bgcolor: 'var(--color-white)',
          borderRadius: 'var(--radius-lg)',
          boxShadow: 'var(--shadow-elevated)',
          p: { xs: 'var(--sp-5)', sm: 'var(--sp-6)' },
        }}
      >
        {/* 琥珀 kizami アクセントバー（catalog .kizami） */}
        <Box
          className="kizami"
          sx={{
            width: 32,
            height: 2,
            bgcolor: 'var(--color-kohaku)',
            borderRadius: '1px',
            mx: 'auto',
            mb: 'var(--sp-5)',
          }}
        />

        {/* catalog 見出し（.login-card h1 相当・variant h6 撤去） */}
        <Box
          component="h1"
          sx={{
            m: 0,
            mb: 'var(--sp-5)',
            color: 'var(--color-ai)',
            fontWeight: 'var(--fw-bold)',
            fontSize: 'var(--fs-16)',
          }}
        >
          {onLogin ? 'アカウントにログイン' : 'パスワードの変更'}
        </Box>

        <Box
          className="login-form"
          sx={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)' }}
        >
          {onLogin ? (
            <>
              <PoField fieldKey="loginId">
                <PoLabel htmlFor="inputId">メールアドレス</PoLabel>
                <PoTextField
                  id="inputId"
                  fullWidth
                  value={Id}
                  onChange={(e) => setId(e.target.value)}
                  InputProps={{ ...({ 'data-testid': 'login-input-id' } as {}) }}
                />
              </PoField>

              <PoField fieldKey="loginPass">
                <PoLabel htmlFor="inputPass">パスワード</PoLabel>
                <PoTextField
                  id="inputPass"
                  fullWidth
                  type={showPassword ? 'text' : 'password'}
                  value={pass}
                  onChange={(e) => setPass(e.target.value)}
                  InputProps={{
                    ...({ 'data-testid': 'login-input-password' } as {}),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() => setShowPassword(!showPassword)}
                          onMouseDown={(e: React.MouseEvent<HTMLButtonElement>) => {
                            e.preventDefault();
                          }}
                          edge="end"
                          size="small"
                        >
                          {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </PoField>
            </>
          ) : (
            <>
              <PoField fieldKey="newPass1">
                <PoLabel htmlFor="inputNewPass1">新規パスワード</PoLabel>
                <PoTextField
                  id="inputNewPass1"
                  fullWidth
                  type={showPassword1 ? 'text' : 'password'}
                  value={newpass1}
                  onChange={(e) => setNewpass1(e.target.value)}
                  InputProps={{
                    ...({ 'data-testid': 'login-input-new-password' } as {}),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() => setShowPassword1(!showPassword1)}
                          onMouseDown={(e: React.MouseEvent<HTMLButtonElement>) => {
                            e.preventDefault();
                          }}
                          edge="end"
                          size="small"
                        >
                          {showPassword1 ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </PoField>

              <PoField fieldKey="newPass2">
                <PoLabel htmlFor="inputNewPass2">新規パスワード（確認用）</PoLabel>
                <PoTextField
                  id="inputNewPass2"
                  fullWidth
                  type={showPassword2 ? 'text' : 'password'}
                  value={newpass2}
                  onChange={(e) => setNewpass2(e.target.value)}
                  InputProps={{
                    ...({ 'data-testid': 'login-input-new-password-confirm' } as {}),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() => setShowPassword2(!showPassword2)}
                          onMouseDown={(e: React.MouseEvent<HTMLButtonElement>) => {
                            e.preventDefault();
                          }}
                          edge="end"
                          size="small"
                        >
                          {showPassword2 ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </PoField>
            </>
          )}

          {message !== "" && (
            <Box
              className="info-alert -error"
              data-testid="login-error-message"
              sx={{ mb: 0 }}
            >
              {message}
            </Box>
          )}

          <PoButton
            type="submit"
            variant="primary"
            fullWidth
            data-testid={onLogin ? 'login-button-submit' : 'login-button-change-password'}
            disabled={loading}
            sx={{
              mt: 'var(--sp-2)',
              height: '42px',
              fontSize: 'var(--fs-14, 14px)',
            }}
          >
            {loading ? (
              <CircularProgress size={20} sx={{ color: 'var(--color-white)' }} />
            ) : (
              onLogin ? 'ログイン' : 'パスワード変更'
            )}
          </PoButton>
        </Box>
      </Box>
    </Box>
  );
}

export default Login;
